import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Alert, Button, Card, Col, Descriptions, Empty, Modal, Popconfirm, Progress, Row, Select, Space, Spin, Switch, Tag, Tooltip, message } from 'antd';
import {
  CheckCircleOutlined,
  CloudServerOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  HddOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { serverAPI } from '../services/api';
import { getStoredZoneId } from '../utils/zone';

const { Option } = Select;

const renderEmptyPanel = (title, subtitle) => (
  <div className="empty-state-panel">
    <div className="empty-state-title">{title}</div>
    {subtitle ? <div className="empty-state-subtitle">{subtitle}</div> : null}
  </div>
);

const EMPTY_SNAPSHOT = {
  servers: [],
  summary: {
    totalServers: 0,
    runningServers: 0,
    stoppedServers: 0,
    totalMemory: 0,
    totalCPU: 0
  },
  fetchedAt: null,
  stale: false,
  fromCache: false,
  cacheAgeMs: 0,
  refreshError: ''
};

const STREAM_STALE_MS = 15000;
const LOG_STREAM_STALE_MS = 10000;

function getAdaptiveRefreshIntervalMs(baseSeconds) {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return Math.max(baseSeconds * 3000, 30000);
  }

  return baseSeconds * 1000;
}

function getAdaptiveLogRefreshIntervalMs() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return 8000;
  }

  return 3000;
}

function ServerMonitor() {
  const [snapshot, setSnapshot] = useState(EMPTY_SNAPSHOT);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(15);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedServerId, setSelectedServerId] = useState(null);
  const [logsVisible, setLogsVisible] = useState(false);
  const [currentLogServerId, setCurrentLogServerId] = useState(null);
  const [logs, setLogs] = useState('');
  const [logLoading, setLogLoading] = useState(false);
  const [logType, setLogType] = useState('startup');
  const [logLines, setLogLines] = useState(100);
  const [logStreamStatus, setLogStreamStatus] = useState(typeof EventSource === 'undefined' ? 'unsupported' : 'idle');
  const [logStreamError, setLogStreamError] = useState('');
  const [lastLogEventAt, setLastLogEventAt] = useState(0);
  const [autoScrollLogs, setAutoScrollLogs] = useState(true);
  const [streamStatus, setStreamStatus] = useState(typeof EventSource === 'undefined' ? 'unsupported' : 'connecting');
  const [streamError, setStreamError] = useState('');
  const [lastStreamEventAt, setLastStreamEventAt] = useState(0);
  const requestRef = useRef({ inFlight: false, lastSnapshotAt: 0 });
  const eventSourceRef = useRef(null);
  const logEventSourceRef = useRef(null);
  const logContentRef = useRef(null);

  const servers = useMemo(() => snapshot.servers || [], [snapshot.servers]);
  const summary = useMemo(() => snapshot.summary || EMPTY_SNAPSHOT.summary, [snapshot.summary]);

  const applyStatusSnapshot = useCallback((nextSnapshot, source) => {
    requestRef.current.lastSnapshotAt = Date.now();
    setSnapshot({
      ...EMPTY_SNAPSHOT,
      ...(nextSnapshot || {}),
      streamMode: source
    });
  }, []);

  const selectedServer = useMemo(
    () => servers.find((server) => server.id === selectedServerId) || null,
    [selectedServerId, servers]
  );

  const currentLogServer = useMemo(
    () => servers.find((server) => server.id === currentLogServerId) || null,
    [currentLogServerId, servers]
  );

  const closeStatusStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const closeLogStream = useCallback(() => {
    if (logEventSourceRef.current) {
      logEventSourceRef.current.close();
      logEventSourceRef.current = null;
    }
  }, []);

  const loadSnapshot = useCallback(async ({ force = false, silent = false } = {}) => {
    if (requestRef.current.inFlight) {
      return;
    }

    const startedAt = Date.now();
    requestRef.current.inFlight = true;
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await serverAPI.getStatus(force ? { force: true } : undefined);
      if (startedAt >= requestRef.current.lastSnapshotAt) {
        applyStatusSnapshot(response.data?.data || {}, 'poll');
      }
      setStreamError('');
    } catch (error) {
      message.error('加载服务器状态失败');
    } finally {
      requestRef.current.inFlight = false;
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [applyStatusSnapshot]);

  const loadLogs = useCallback(async (serverId, type = logType, lines = logLines, options = {}) => {
    const { silent = false } = options;
    if (!serverId) {
      return;
    }

    setLogLoading(true);
    try {
      const response = await serverAPI.getLogs(serverId, { type, lines });
      setLogs(response.data?.data?.logs || '');
      setLogType(type);
      setLogLines(lines);
      setLogStreamError('');
    } catch (error) {
      if (!silent) {
        message.error('加载日志失败');
        setLogs('无法加载日志文件');
      }
      setLogStreamError('日志流不可用，当前使用低频拉取');
    } finally {
      setLogLoading(false);
    }
  }, [logLines, logType]);

  const openLogs = useCallback(async (server) => {
    setCurrentLogServerId(server.id);
    setLogsVisible(true);
    setLogs('');
    setLogType('startup');
    setLogLines(100);
    setLogStreamError('');
    setLogStreamStatus(typeof EventSource === 'undefined' ? 'unsupported' : 'connecting');
  }, []);

  const connectLogStream = useCallback(() => {
    const token = localStorage.getItem('token');

    if (!logsVisible || !currentLogServerId) {
      return;
    }

    if (!token || typeof EventSource === 'undefined') {
      setLogStreamStatus(typeof EventSource === 'undefined' ? 'unsupported' : 'unavailable');
      return;
    }

    closeLogStream();
    setLogLoading(true);
    setLogStreamStatus('connecting');

    const stream = new EventSource(serverAPI.getLogStreamUrl(
      currentLogServerId,
      { type: logType, lines: logLines, zoneId: getStoredZoneId() },
      token
    ));
    logEventSourceRef.current = stream;

    stream.onopen = () => {
      if (logEventSourceRef.current !== stream) {
        return;
      }
      setLogStreamStatus('connected');
      setLogStreamError('');
    };

    stream.addEventListener('log-snapshot', (event) => {
      if (logEventSourceRef.current !== stream) {
        return;
      }

      try {
        const payload = JSON.parse(event.data || '{}');
        setLogs(payload.logs || '');
        setLastLogEventAt(Date.now());
        setLogLoading(false);
        setLogStreamStatus('connected');
        setLogStreamError('');
      } catch (error) {
        setLogStreamStatus('reconnecting');
        setLogStreamError('实时日志解析失败，已切换低频拉取');
      }
    });

    stream.addEventListener('log-error', (event) => {
      if (logEventSourceRef.current !== stream) {
        return;
      }

      let payload = {};
      try {
        payload = JSON.parse(event.data || '{}');
      } catch (error) {}

      setLogStreamStatus('reconnecting');
      setLogStreamError(payload.message || '实时日志通道暂时不可用');
    });

    stream.onerror = () => {
      if (logEventSourceRef.current !== stream) {
        return;
      }
      setLogStreamStatus('reconnecting');
    };
  }, [closeLogStream, currentLogServerId, logLines, logType, logsVisible]);

  const connectStatusStream = useCallback(() => {
    const token = localStorage.getItem('token');

    if (!token || typeof EventSource === 'undefined') {
      setStreamStatus(typeof EventSource === 'undefined' ? 'unsupported' : 'unavailable');
      return;
    }

    closeStatusStream();
    setStreamStatus('connecting');

    const stream = new EventSource(serverAPI.getStatusStreamUrl(token, getStoredZoneId()));
    eventSourceRef.current = stream;

    stream.onopen = () => {
      if (eventSourceRef.current !== stream) {
        return;
      }
      setStreamStatus('connected');
      setStreamError('');
    };

    stream.addEventListener('snapshot', (event) => {
      if (eventSourceRef.current !== stream) {
        return;
      }

      try {
        const nextSnapshot = JSON.parse(event.data || '{}');
        applyStatusSnapshot(nextSnapshot, 'push');
        setLastStreamEventAt(Date.now());
        setStreamStatus('connected');
        setStreamError('');
        setLoading(false);
        setRefreshing(false);
      } catch (error) {
        setStreamStatus('reconnecting');
        setStreamError('实时数据解析失败，已切换回智能轮询');
      }
    });

    stream.addEventListener('status-error', (event) => {
      if (eventSourceRef.current !== stream) {
        return;
      }

      let payload = {};
      try {
        payload = JSON.parse(event.data || '{}');
      } catch (error) {}

      setStreamStatus('reconnecting');
      setStreamError(payload.message || '实时通道暂时不可用，已切换回智能轮询');
    });

    stream.onerror = () => {
      if (eventSourceRef.current !== stream) {
        return;
      }

      setStreamStatus('reconnecting');
    };
  }, [applyStatusSnapshot, closeStatusStream]);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  useEffect(() => {
    connectStatusStream();

    return () => {
      closeStatusStream();
    };
  }, [closeStatusStream, connectStatusStream]);

  useEffect(() => {
    if (!lastStreamEventAt || streamStatus !== 'connected') {
      return undefined;
    }

    const timerId = setInterval(() => {
      if (Date.now() - lastStreamEventAt > STREAM_STALE_MS) {
        setStreamStatus('stale');
      }
    }, 3000);

    return () => clearInterval(timerId);
  }, [lastStreamEventAt, streamStatus]);

  useEffect(() => {
    if (streamStatus !== 'stale') {
      return undefined;
    }

    closeStatusStream();
    const timerId = setTimeout(() => {
      connectStatusStream();
    }, 1000);

    return () => clearTimeout(timerId);
  }, [closeStatusStream, connectStatusStream, streamStatus]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && streamStatus !== 'connected') {
        loadSnapshot({ silent: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadSnapshot, streamStatus]);

  useEffect(() => {
    if (!autoRefresh || streamStatus === 'connected') {
      return undefined;
    }

    let timerId = null;
    let disposed = false;

    const scheduleNext = () => {
      timerId = setTimeout(async () => {
        await loadSnapshot({ silent: true });
        if (!disposed) {
          scheduleNext();
        }
      }, getAdaptiveRefreshIntervalMs(refreshInterval));
    };

    scheduleNext();

    return () => {
      disposed = true;
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [autoRefresh, refreshInterval, loadSnapshot, streamStatus]);

  useEffect(() => {
    if (!logsVisible || !currentLogServerId) {
      closeLogStream();
      setLogStreamStatus(typeof EventSource === 'undefined' ? 'unsupported' : 'idle');
      return undefined;
    }

    connectLogStream();

    return () => {
      closeLogStream();
    };
  }, [closeLogStream, connectLogStream, currentLogServerId, logLines, logType, logsVisible]);

  useEffect(() => {
    if (!lastLogEventAt || logStreamStatus !== 'connected') {
      return undefined;
    }

    const timerId = setInterval(() => {
      if (Date.now() - lastLogEventAt > LOG_STREAM_STALE_MS) {
        setLogStreamStatus('stale');
      }
    }, 3000);

    return () => clearInterval(timerId);
  }, [lastLogEventAt, logStreamStatus]);

  useEffect(() => {
    if (logStreamStatus !== 'stale') {
      return undefined;
    }

    closeLogStream();
    const timerId = setTimeout(() => {
      connectLogStream();
    }, 1000);

    return () => clearTimeout(timerId);
  }, [closeLogStream, connectLogStream, logStreamStatus]);

  useEffect(() => {
    if (!logsVisible || !currentLogServerId || logStreamStatus === 'connected') {
      return undefined;
    }

    let timerId = null;
    let disposed = false;

    const scheduleNext = () => {
      timerId = setTimeout(async () => {
        await loadLogs(currentLogServerId, logType, logLines, { silent: true });
        if (!disposed) {
          scheduleNext();
        }
      }, getAdaptiveLogRefreshIntervalMs());
    };

    loadLogs(currentLogServerId, logType, logLines, { silent: true });
    scheduleNext();

    return () => {
      disposed = true;
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [currentLogServerId, logLines, logStreamStatus, logType, logsVisible, loadLogs]);

  useEffect(() => {
    if (!logsVisible || !autoScrollLogs || !logContentRef.current) {
      return;
    }

    logContentRef.current.scrollTop = logContentRef.current.scrollHeight;
  }, [autoScrollLogs, logs, logsVisible]);

  const refreshNow = useCallback(async () => {
    await loadSnapshot({ force: true, silent: true });
  }, [loadSnapshot]);

  const handleStart = useCallback(async (serverId) => {
    try {
      await serverAPI.start(serverId);
      message.success('服务器启动成功');
      await loadSnapshot({ force: true, silent: true });
    } catch (error) {
      message.error('启动服务器失败');
    }
  }, [loadSnapshot]);

  const handleStop = useCallback(async (serverId) => {
    try {
      await serverAPI.stop(serverId);
      message.success('服务器停止成功');
      await loadSnapshot({ force: true, silent: true });
    } catch (error) {
      message.error('停止服务器失败');
    }
  }, [loadSnapshot]);

  const handleRestart = useCallback(async (serverId) => {
    try {
      await serverAPI.restart(serverId);
      message.success('服务器重启成功');
      await loadSnapshot({ force: true, silent: true });
    } catch (error) {
      message.error('重启服务器失败');
    }
  }, [loadSnapshot]);

  const handleStartAll = useCallback(() => {
    Modal.confirm({
      title: '确认启动所有服务器？',
      content: '这将依次启动所有服务器。',
      onOk: async () => {
        try {
          await serverAPI.startAll();
          message.success('所有服务器启动成功');
          await loadSnapshot({ force: true, silent: true });
        } catch (error) {
          message.error('启动所有服务器失败');
        }
      }
    });
  }, [loadSnapshot]);

  const handleStopAll = useCallback(() => {
    Modal.confirm({
      title: '确认停止所有服务器？',
      content: '警告：这将停止所有运行中的服务器。',
      okText: '确认停止',
      okType: 'danger',
      onOk: async () => {
        try {
          await serverAPI.stopAll();
          message.success('所有服务器停止成功');
          await loadSnapshot({ force: true, silent: true });
        } catch (error) {
          message.error('停止所有服务器失败');
        }
      }
    });
  }, [loadSnapshot]);

  const fetchedAtText = snapshot.fetchedAt
    ? new Date(snapshot.fetchedAt).toLocaleString('zh-CN', { hour12: false })
    : '未刷新';

  const monitorMode = streamStatus === 'connected' ? '实时推送' : '智能轮询';
  const monitorModeTag = streamStatus === 'connected'
    ? { color: 'success', text: '实时推送' }
    : streamStatus === 'connecting'
      ? { color: 'processing', text: '连接中' }
      : streamStatus === 'unsupported'
        ? { color: 'default', text: '浏览器不支持' }
        : { color: 'warning', text: '智能回退中' };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="page-header-main">
          <div className="page-title">服务器监控</div>
          <div className="page-subtitle">统一查看节点状态、资源占用、推送链路和日志流，支持直接在列表执行启动、停止和重启。</div>
        </div>
        <div className="page-header-actions">
          <Tag color={monitorModeTag.color}>{monitorModeTag.text}</Tag>
          <Tag>{summary.totalServers} 台节点</Tag>
        </div>
      </div>

      <div className="page-note">
        <div className="page-note-text">
          <span className="page-note-strong">这页优先保留后台运维最常用的动作。</span>
          先看整体状态和资源，再决定是否批量启动、停止或进入日志排查。
        </div>
        <span style={{ color: '#8a94a6', fontSize: 12 }}>快照时间 {fetchedAtText}</span>
      </div>

      <div className="compact-stat-row">
        <div className="compact-stat">
          <div className="compact-stat-label">运行中</div>
          <div className="compact-stat-value" style={{ color: '#16a34a' }}>{summary.runningServers}</div>
          <div className="table-cell-secondary"><CheckCircleOutlined /> / {summary.totalServers} 台节点</div>
        </div>
        <div className="compact-stat">
          <div className="compact-stat-label">已停止</div>
          <div className="compact-stat-value" style={{ color: '#dc2626' }}>{summary.stoppedServers}</div>
          <div className="table-cell-secondary"><CloseCircleOutlined /> 等待人工处理</div>
        </div>
        <div className="compact-stat">
          <div className="compact-stat-label">总内存</div>
          <div className="compact-stat-value" style={{ color: '#2563eb' }}>{summary.totalMemory}</div>
          <div className="table-cell-secondary"><HddOutlined /> MB</div>
        </div>
        <div className="compact-stat">
          <div className="compact-stat-label">总 CPU</div>
          <div className="compact-stat-value" style={{ color: '#7c3aed' }}>{summary.totalCPU}</div>
          <div className="table-cell-secondary"><ThunderboltOutlined /> %</div>
        </div>
      </div>

      <Card className="filter-card">
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <div className="action-group">
              <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleStartAll}>启动所有</Button>
              <Button danger icon={<StopOutlined />} onClick={handleStopAll}>停止所有</Button>
              <Button icon={<ReloadOutlined />} onClick={refreshNow} loading={refreshing}>立即刷新</Button>
            </div>
          </Col>
          <Col>
            <Space wrap align="center">
              <span style={{ color: '#5b6472', fontSize: 12 }}>监控模式 {monitorMode}</span>
              <span style={{ color: '#5b6472', fontSize: 12 }}>缓存年龄 {Math.round((snapshot.cacheAgeMs || 0) / 1000)} 秒</span>
              <span>自动刷新</span>
              <Switch checked={autoRefresh} onChange={setAutoRefresh} />
              <Select value={refreshInterval} onChange={setRefreshInterval} style={{ width: 120 }} disabled={!autoRefresh}>
                <Option value={5}>5秒</Option>
                <Option value={15}>15秒</Option>
                <Option value={30}>30秒</Option>
                <Option value={60}>60秒</Option>
              </Select>
            </Space>
          </Col>
        </Row>
      </Card>

      {(snapshot.stale || snapshot.refreshError) && (
        <Alert
          type={snapshot.refreshError ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
          message={snapshot.refreshError ? '状态刷新失败，当前展示缓存快照' : '当前展示缓存快照'}
          description={snapshot.refreshError || `数据来源: ${snapshot.fromCache ? '缓存' : '实时采集'}`}
        />
      )}

      {streamStatus !== 'connected' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="实时推送暂不可用，当前已切换为智能轮询"
          description={streamError || '页面可继续使用，恢复连接后会自动切回实时模式。'}
        />
      )}

      <Card className="table-card" title="节点列表" extra={<span style={{ color: '#8a94a6', fontSize: 12 }}>支持列表直接执行启动、停止、重启和日志查看</span>}>
        {loading ? (
          <div className="server-monitor-loading"><Spin /></div>
        ) : servers.length > 0 ? (
          <div className="server-monitor-list">
            {servers.map((record) => (
              <div className="server-monitor-item" key={record.id}>
                <div className="server-monitor-item-main">
                  <div className="server-monitor-item-title">
                    <CloudServerOutlined style={{ color: record.isRunning ? '#52c41a' : '#ff4d4f' }} />
                    <span>{record.name}</span>
                  </div>
                  <div className="server-monitor-item-subtitle">{record.description || '未填写节点说明'}</div>
                </div>

                <div className="server-monitor-item-block">
                  <div className="server-monitor-item-label">状态</div>
                  <div className="server-monitor-item-value">
                    <Tag color={record.isRunning ? 'success' : 'error'}>
                      {record.isRunning ? '运行中' : '已停止'}
                    </Tag>
                  </div>
                  <div className="server-monitor-item-meta">
                    {record.statusSource === 'process'
                      ? <Tag color="blue">process</Tag>
                      : record.statusSource === 'screen'
                        ? <Tag color="gold">screen</Tag>
                        : <Tag>none</Tag>}
                  </div>
                </div>

                <div className="server-monitor-item-block">
                  <div className="server-monitor-item-label">连接与进程</div>
                  <div className="server-monitor-item-value">端口 {record.port || '-'}</div>
                  <div className="server-monitor-item-meta">PID {record.pid || '-'}</div>
                  <div className="server-monitor-item-meta">{record.isRunning ? `运行时间 ${record.uptime || '未知'}` : '当前未运行'}</div>
                </div>

                <div className="server-monitor-item-block">
                  <div className="server-monitor-item-label">资源占用</div>
                  <div className="server-monitor-item-value">{record.isRunning ? `${record.memory || 0} MB` : '-'}</div>
                  <div className="server-monitor-item-meta">{record.isRunning ? `${(parseFloat(record.cpu) || 0).toFixed(1)}% CPU` : '停止状态无资源占用'}</div>
                </div>

                <div className="server-monitor-item-actions">
                  <Tooltip title="查看详情">
                    <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelectedServerId(record.id); setDetailVisible(true); }} />
                  </Tooltip>
                  {!record.isRunning ? (
                    <Popconfirm title={`确定启动 ${record.name} 吗？`} onConfirm={() => handleStart(record.id)} okText="确定" cancelText="取消">
                      <Button type="primary" size="small" icon={<PlayCircleOutlined />}>启动</Button>
                    </Popconfirm>
                  ) : (
                    <Popconfirm title={`确定停止 ${record.name} 吗？`} onConfirm={() => handleStop(record.id)} okText="确定" cancelText="取消">
                      <Button danger size="small" icon={<StopOutlined />}>停止</Button>
                    </Popconfirm>
                  )}
                  <Popconfirm title={`确定重启 ${record.name} 吗？`} onConfirm={() => handleRestart(record.id)} okText="确定" cancelText="取消">
                    <Button size="small" icon={<ReloadOutlined />} disabled={!record.isRunning}>重启</Button>
                  </Popconfirm>
                  <Button size="small" icon={<FileTextOutlined />} onClick={() => openLogs(record)}>日志</Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          renderEmptyPanel('暂无服务器数据', '当前未获取到可展示的节点信息。')
        )}
      </Card>

      <Modal
        title={selectedServer ? `服务器详情 - ${selectedServer.name}` : '服务器详情'}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={<Button onClick={() => setDetailVisible(false)}>关闭</Button>}
      >
        {selectedServer ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="服务器名称">{selectedServer.name}</Descriptions.Item>
            <Descriptions.Item label="描述">{selectedServer.description}</Descriptions.Item>
            <Descriptions.Item label="端口">{selectedServer.port || '-'}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={selectedServer.isRunning ? 'success' : 'error'}>
                {selectedServer.isRunning ? '运行中' : '已停止'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="PID">{selectedServer.pid || '-'}</Descriptions.Item>
            <Descriptions.Item label="运行时间">{selectedServer.uptime || '-'}</Descriptions.Item>
            <Descriptions.Item label="采集来源">{selectedServer.statusSource || '-'}</Descriptions.Item>
            <Descriptions.Item label="内存使用">
              <Progress percent={Math.min((selectedServer.memory || 0) / 20, 100)} status="active" />
              <span>{selectedServer.memory || 0} MB</span>
            </Descriptions.Item>
            <Descriptions.Item label="CPU使用">
              <Progress percent={Math.min(parseFloat(selectedServer.cpu) || 0, 100)} status={selectedServer.cpu > 80 ? 'exception' : 'normal'} />
              <span>{(parseFloat(selectedServer.cpu) || 0).toFixed(1)}%</span>
            </Descriptions.Item>
            <Descriptions.Item label="进程信息">{selectedServer.processInfo || '-'}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty description="未找到服务器详情" />
        )}
      </Modal>

      <Modal
        title={
          <Space>
            <FileTextOutlined />
            <span>服务器日志 - {currentLogServer?.name || currentLogServerId || ''}</span>
            <Tag color={logStreamStatus === 'connected' ? 'success' : logStreamStatus === 'connecting' ? 'processing' : 'warning'}>
              {logStreamStatus === 'connected' ? '实时日志' : logStreamStatus === 'connecting' ? '连接中' : '回退拉取'}
            </Tag>
          </Space>
        }
        open={logsVisible}
        onCancel={() => {
          setLogsVisible(false);
          setLogStreamError('');
        }}
        width={1000}
        footer={
          <Space>
            <Select value={logType} onChange={setLogType} style={{ width: 120 }}>
              <Option value="startup">启动日志</Option>
              <Option value="error">错误日志</Option>
              <Option value="server">服务器日志</Option>
            </Select>
            <Select value={logLines} onChange={setLogLines} style={{ width: 100 }}>
              <Option value={50}>50行</Option>
              <Option value={100}>100行</Option>
              <Option value={200}>200行</Option>
              <Option value={500}>500行</Option>
            </Select>
            <span>自动滚动</span>
            <Switch size="small" checked={autoScrollLogs} onChange={setAutoScrollLogs} />
            <Button icon={<ReloadOutlined />} onClick={() => loadLogs(currentLogServerId, logType, logLines)} loading={logLoading}>刷新日志</Button>
            <Button onClick={() => setLogsVisible(false)}>关闭</Button>
          </Space>
        }
      >
        {logStreamError && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="日志实时通道暂不可用"
            description={logStreamError}
          />
        )}
        {logLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <pre ref={logContentRef} style={{ maxHeight: 600, overflow: 'auto', padding: 16, background: '#1e1e1e', color: '#d4d4d4', borderRadius: 4, fontSize: 12, fontFamily: 'Consolas, Monaco, monospace', lineHeight: 1.5 }}>
            {logs}
          </pre>
        )}
      </Modal>
    </div>
  );
}

export default ServerMonitor;
